import { useState, useEffect, useMemo, Suspense, lazy, ReactNode } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Soft UI Dashboard React themes
import { createAppTheme } from "assets/theme";
import { createAppThemeRTL } from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider, EmotionCache } from "@emotion/react";
import createCache from "@emotion/cache";
import { authApi } from "api";

// Soft UI Dashboard React routes
import routes from "routes";
const UserDetail = lazy(() => import("layouts/users/detail"));
const TicketDetail = lazy(() => import("layouts/tickets/detail"));

// Soft UI Dashboard React examples
import Sidenav from "components/Sidenav";
import GlobalPageLoader from "components/GlobalPageLoader";

// Soft UI Dashboard React contexts
import { useSoftUIController, setMiniSidenav } from "context";

// Redux
import { useAppDispatch, useAppSelector } from "store/hooks";
import { AdminRole, clearAuth, setAdmin, setAuthLoading } from "store/slices/authSlice";
import { clearUsersLookup, setUsersLookup } from "store/slices/appSlice";

// Images
const brand = "https://files.payunif.com/waaconnect/waaconnect.png";

interface RouteType {
  type: string;
  name?: string;
  key: string;
  route?: string;
  href?: string;
  icon?: ReactNode;
  component?: ReactNode;
  roles?: AdminRole[];
  collapse?: RouteType[];
}

export default function App() {
  const [controller, softDispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, sidenavColor, colorMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState<EmotionCache | null>(null);
  const { pathname } = useLocation();
  const reduxDispatch = useAppDispatch();

  // Auth state
  const token = useAppSelector((state) => state.auth.token);
  const adminRole = useAppSelector((state) => state.auth.admin?.role);
  const isAuthLoading = useAppSelector((state) => state.auth.isAuthLoading);
  const globalLoadingCount = useAppSelector((state) => state.app.globalLoadingCount);
  const usersLookup = useAppSelector((state) => state.app.usersLookup);
  const isAuthenticated = Boolean(token);
  const isGlobalLoading = globalLoadingCount > 0;
  const hasUsersLookup = Object.keys(usersLookup).length > 0;

  const hasRoleAccess = (route: RouteType) => {
    if (!route.roles || route.roles.length === 0) return true;
    if (!adminRole) return false;
    return route.roles.includes(adminRole);
  };

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin as any],
    });

    setRtlCache(cacheRtl);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const bootstrapAuth = async () => {
      const savedToken = localStorage.getItem("waa_token");

      // Pas de token → déjà géré synchroniquement dans authSlice (isAuthLoading=false)
      if (!savedToken) return;

      // Token présent → on valide côté serveur (isAuthLoading=true depuis le slice)
      try {
        const profile = await authApi.getProfile();
        if (!isMounted) return;
        reduxDispatch(
          setAdmin({
            id: profile.id,
            name: profile.name,
            email: profile.email,
            role: profile.role,
          })
        );
      } catch {
        if (isMounted) {
          reduxDispatch(clearAuth());
        }
      } finally {
        if (isMounted) {
          reduxDispatch(setAuthLoading(false));
        }
      }
    };

    bootstrapAuth();

    return () => {
      isMounted = false;
    };
  }, [reduxDispatch]);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(softDispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(softDispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  // Warm-up du mapping users (id -> nom/email) pour éviter les mappings DB répétés côté API
  useEffect(() => {
    if (!isAuthenticated) {
      reduxDispatch(clearUsersLookup());
      return;
    }

    if (hasUsersLookup) return;

    const cached = authApi.getCachedUsersLookup();
    if (cached && Object.keys(cached).length > 0) {
      reduxDispatch(setUsersLookup(cached));
      return;
    }

    authApi
      .refreshUsersLookup()
      .then((lookup) => {
        if (lookup && Object.keys(lookup).length > 0) {
          reduxDispatch(setUsersLookup(lookup));
        }
      })
      .catch(() => {
        // Pas bloquant: le cache se remplira à la prochaine page qui en a besoin.
      });
  }, [isAuthenticated, hasUsersLookup, reduxDispatch]);

  const getRoutes = (allRoutes: RouteType[]): ReactNode[] =>
    allRoutes
      .map((route) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          const isAuth = route.route.startsWith("/authentication");
          const element = isAuth ? (
            // Auth page: redirect to dashboard if already logged in
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              (route.component as any)
            )
          ) : // Protected page: redirect to sign-in if not logged in
          !isAuthenticated ? (
            <Navigate to="/authentication/sign-in" replace />
          ) : !hasRoleAccess(route) ? (
            <Navigate to="/dashboard" replace />
          ) : (
            (route.component as any)
          );
          return <Route path={route.route} element={element} key={route.key} />;
        }

        return null;
      })
      .flat();

  const appTheme = useMemo(() => createAppTheme(colorMode), [colorMode]);
  const appThemeRTL = useMemo(() => createAppThemeRTL(colorMode), [colorMode]);
  const sidebarRoutes = useMemo(
    () =>
      routes.filter((route) => {
        if (route.route?.startsWith("/authentication")) {
          return false;
        }
        return hasRoleAccess(route);
      }),
    [adminRole]
  );

  if (isAuthLoading) {
    return <GlobalPageLoader open />;
  }

  const configs = (
    <>
      {isAuthenticated && layout === "dashboard" && (
        <Sidenav
          color={sidenavColor}
          brand={brand}
          brandName="WaaConnect Admin"
          routes={sidebarRoutes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache!}>
      <ThemeProvider theme={appThemeRTL}>
        <CssBaseline />
        <GlobalPageLoader open={isGlobalLoading} />
        {configs}
        <Suspense fallback={<GlobalPageLoader open />}>
          <Routes>
            {getRoutes(routes)}
            <Route
              path="/users/:id"
              element={
                !isAuthenticated ? (
                  <Navigate to="/authentication/sign-in" replace />
                ) : adminRole !== "SUPER_ADMIN" ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <UserDetail />
                )
              }
            />
            <Route
              path="/tickets/:id"
              element={
                !isAuthenticated ? (
                  <Navigate to="/authentication/sign-in" replace />
                ) : (
                  <TicketDetail />
                )
              }
            />
            <Route
              path="*"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <Navigate to="/authentication/sign-in" />
                )
              }
            />
          </Routes>
        </Suspense>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <GlobalPageLoader open={isGlobalLoading} />
      {configs}
      <Suspense fallback={<GlobalPageLoader open />}>
        <Routes>
          {getRoutes(routes)}
          <Route
            path="/users/:id"
            element={
              !isAuthenticated ? (
                <Navigate to="/authentication/sign-in" replace />
              ) : adminRole !== "SUPER_ADMIN" ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <UserDetail />
              )
            }
          />
          <Route
            path="/tickets/:id"
            element={
              !isAuthenticated ? (
                <Navigate to="/authentication/sign-in" replace />
              ) : (
                <TicketDetail />
              )
            }
          />
          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <Navigate to="/authentication/sign-in" />
              )
            }
          />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}
